// ──────────────────────────────────────────────────────────────────────────
// Risk Applicability Matrix — canonical Admin / GRC Secretariat view.
//
// The page implements the user's spec table verbatim:
//   | Risk ID | Risk Item | Operating Head | Division | Applicable |
//
// Each row is a Risk × OH × Division triple. Applicability is owned at the
// (Risk × OH) level; toggling a cell flips applicability for all sibling
// divisions under that OH (since divisions inherit from their OH).
//
// Admin-only mutating UI. Read-only render for OH / DivH / DH.
// ──────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { ORG_TREE } from '../data/orgTree';
import {
  CheckCircle2, XCircle, AlertTriangle, Search, Lock, Flame, Layers,
  Map as MapIcon, ShieldCheck, Inbox, Send, Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function buildRows(riskUniverse, applicability) {
  const rows = [];
  Object.values(ORG_TREE).forEach((oh) => {
    Object.values(oh.divisions).forEach((div) => {
      riskUniverse.forEach((risk) => {
        const applicable = (applicability[risk.id] || []).includes(oh.id);
        rows.push({
          rowId: `${risk.id}-${oh.id}-${div.id}`,
          riskId: risk.id,
          riskItem: risk.description,
          classification: risk.minorClassification || risk.classification || '—',
          ohId: oh.id,
          ohLabel: oh.label,
          ohColor: oh.color,
          divId: div.id,
          divLabel: div.label,
          applicable,
        });
      });
    });
  });
  return rows;
}

export default function ApplicabilityMatrixPage() {
  const navigate = useNavigate();
  const { user, riskUniverse, applicability, toggleApplicability, riskPublished, publishRiskAssessment, seedMode, setRiskSeedMode } = useHGS();
  const [query, setQuery] = useState('');
  const [filterOh, setFilterOh] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | applicable | not-applicable | unmapped
  const [groupByClass, setGroupByClass] = useState(true);

  const isAdmin = user?.role === 'admin';

  // Canonical row list (Risk × OH × Division)
  const allRows = useMemo(() => buildRows(riskUniverse || [], applicability || {}), [riskUniverse, applicability]);

  // Risk-level rollup (for the side panel: unmapped + high-applicability)
  const riskRollup = useMemo(() => {
    return (riskUniverse || []).map((r) => {
      const ohs = applicability?.[r.id] || [];
      const divs = ohs.reduce((n, ohId) => n + Object.keys(ORG_TREE[ohId]?.divisions || {}).length, 0);
      return {
        id: r.id,
        title: r.description,
        classification: r.minorClassification || r.classification || '—',
        ohCount: ohs.length,
        divCount: divs,
        ohs,
      };
    });
  }, [riskUniverse, applicability]);

  const unmappedRisks = useMemo(() => riskRollup.filter((r) => r.ohCount === 0), [riskRollup]);
  const highApplicabilityAreas = useMemo(() => {
    // OHs ranked by number of risks applicable to them
    const counts = {};
    Object.keys(ORG_TREE).forEach((id) => { counts[id] = 0; });
    Object.entries(applicability || {}).forEach(([, ohs]) => {
      (ohs || []).forEach((id) => { if (counts[id] !== undefined) counts[id] += 1; });
    });
    return Object.entries(counts)
      .map(([ohId, count]) => ({ ohId, label: ORG_TREE[ohId]?.label, count, color: ORG_TREE[ohId]?.color }))
      .sort((a, b) => b.count - a.count);
  }, [applicability]);

  // Filtered rows
  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (filterOh && r.ohId !== filterOh) return false;
      if (filterStatus === 'applicable' && !r.applicable) return false;
      if (filterStatus === 'not-applicable' && r.applicable) return false;
      if (filterStatus === 'unmapped') {
        const ohs = applicability?.[r.riskId] || [];
        if (ohs.length > 0) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        if (!`${r.riskId} ${r.riskItem} ${r.classification} ${r.ohLabel} ${r.divLabel}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, filterOh, filterStatus, query, applicability]);

  // Group by classification for nicer rendering (optional)
  const grouped = useMemo(() => {
    if (!groupByClass) return [{ key: 'All Risks', rows }];
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.classification)) map.set(r.classification, []);
      map.get(r.classification).push(r);
    });
    return Array.from(map.entries()).map(([key, rs]) => ({ key, rows: rs }));
  }, [rows, groupByClass]);

  const exportCsv = () => {
    const header = ['Risk ID', 'Risk Item', 'Classification', 'Operating Head', 'Division', 'Applicable'];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      lines.push([
        r.riskId,
        `"${(r.riskItem || '').replace(/"/g, '""')}"`,
        `"${r.classification}"`,
        `"${r.ohLabel}"`,
        `"${r.divLabel}"`,
        r.applicable ? 'Yes' : 'No',
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risk_applicability_matrix_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported CSV');
  };

  const handlePublish = () => {
    if (!publishRiskAssessment) return toast.error('Publish handler unavailable');
    publishRiskAssessment();
    toast.success('Applicability mapping published · downstream tasks generated');
  };

  return (
    <div>
      <TopBar
        title="Risk Applicability Matrix"
        subtitle="Admin / GRC Secretariat · centrally configured · Operating Heads cannot self-assign risks"
        breadcrumbs={['HGS', 'Admin', 'Applicability Matrix']}
      />
      <div className="p-8 space-y-5">

        {/* Identity banner */}
        <div className="flex items-start gap-4 p-5 rounded-xl bg-gradient-to-r from-slate-900 to-[#0a1830] text-white" data-testid="matrix-banner">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center"><MapIcon className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">RISK MANAGEMENT SECRETARIAT · APPLICABILITY OWNERSHIP</div>
            <div className="text-base font-bold flex items-center gap-2">
              Risk × Operating Head × Division mapping
              <span className={cn('px-1.5 py-0.5 rounded text-[9px] font-bold uppercase',
                seedMode === 'demo-curated' ? 'bg-amber-400 text-slate-900' : 'bg-slate-700 text-slate-200')}>
                {seedMode === 'demo-curated' ? 'Curated 10-risk demo' : 'HMSI Full 45'}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {riskUniverse.length} master risks · {Object.keys(ORG_TREE).length} Operating Heads · {Object.values(ORG_TREE).reduce((n, oh) => n + Object.keys(oh.divisions).length, 0)} divisions total · {unmappedRisks.length} unmapped
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {isAdmin && (
              <div className="inline-flex rounded-lg border border-white/20 overflow-hidden" data-testid="seed-mode-toggle">
                <button
                  onClick={() => setRiskSeedMode('hmsi-full')}
                  data-testid="seed-mode-hmsi"
                  className={cn('px-2.5 py-1 text-[10px] font-bold',
                    seedMode !== 'demo-curated' ? 'bg-white text-slate-900' : 'bg-transparent text-slate-300 hover:bg-white/10')}
                >
                  HMSI Full
                </button>
                <button
                  onClick={() => setRiskSeedMode('demo-curated')}
                  data-testid="seed-mode-curated"
                  className={cn('px-2.5 py-1 text-[10px] font-bold',
                    seedMode === 'demo-curated' ? 'bg-amber-400 text-slate-900' : 'bg-transparent text-slate-300 hover:bg-white/10')}
                >
                  Curated Demo
                </button>
              </div>
            )}
            <div className="inline-flex items-center gap-2">
              <span className={cn('px-2 py-0.5 rounded text-[10px] font-bold uppercase',
                riskPublished?.published ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white')} data-testid="publish-state">
                {riskPublished?.published ? 'Published' : 'Draft'}
              </span>
              {isAdmin && (
                <button
                  onClick={handlePublish}
                  disabled={riskPublished?.published}
                  data-testid="matrix-publish"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#CC0000] hover:bg-red-700 text-white text-[11px] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-3 h-3" />
                  Publish mapping
                </button>
              )}
            </div>
            <button
              onClick={exportCsv}
              data-testid="matrix-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-[11px] font-bold"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </button>
          </div>
        </div>

        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3" data-testid="matrix-readonly-banner">
            <Lock className="w-4 h-4 text-amber-700 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-900">Read-only view</p>
              <p className="text-[11px] text-amber-700">
                Risk applicability is owned by the GRC Secretariat. Operating Heads cannot self-assign risks. You see the matrix to understand which risks land in your scope.
              </p>
            </div>
          </div>
        )}

        {/* Insight strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Unmapped risks */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="unmapped-risks-card">
            <div className="px-4 py-3 border-b border-amber-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <div>
                <h3 className="text-xs font-bold text-slate-900">Unmapped Risks</h3>
                <p className="text-[10px] text-slate-500">No OH currently owns these</p>
              </div>
              <span className="ml-auto text-xl font-bold tabular-nums text-amber-900">{unmappedRisks.length}</span>
            </div>
            <div className="max-h-40 overflow-y-auto divide-y divide-amber-100">
              {unmappedRisks.length === 0 ? (
                <div className="p-3 text-center text-[10px] text-slate-500">All risks have at least one Operating Head owner.</div>
              ) : unmappedRisks.slice(0, 12).map((r) => (
                <div key={r.id} className="px-4 py-1.5 flex items-center gap-2 text-[10px]" data-testid={`unmapped-${r.id}`}>
                  <span className="font-mono font-bold text-amber-800">{r.id}</span>
                  <span className="text-slate-700 truncate flex-1">{r.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* High-applicability areas (OHs with most risks) */}
          <div className="rounded-xl border border-red-200 bg-red-50/40 overflow-hidden" data-testid="high-applicability-card">
            <div className="px-4 py-3 border-b border-red-100 flex items-center gap-2">
              <Flame className="w-4 h-4 text-red-600" />
              <div>
                <h3 className="text-xs font-bold text-slate-900">High-Applicability Areas</h3>
                <p className="text-[10px] text-slate-500">OHs ranked by risk load</p>
              </div>
            </div>
            <div className="divide-y divide-red-100">
              {highApplicabilityAreas.map((a) => (
                <button
                  key={a.ohId}
                  onClick={() => setFilterOh(filterOh === a.ohId ? '' : a.ohId)}
                  data-testid={`ha-${a.ohId}`}
                  className={cn('w-full px-4 py-2 flex items-center gap-2 hover:bg-red-50 transition text-left',
                    filterOh === a.ohId && 'bg-red-100')}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                  <span className="text-[11px] font-semibold text-slate-800 flex-1 truncate">{a.label}</span>
                  <span className="text-sm font-bold tabular-nums text-red-700">{a.count}</span>
                  <span className="text-[9px] text-slate-500">risks</span>
                </button>
              ))}
            </div>
          </div>

          {/* Classification load */}
          <div className="rounded-xl border border-violet-200 bg-violet-50/40 overflow-hidden" data-testid="classification-card">
            <div className="px-4 py-3 border-b border-violet-100 flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-700" />
              <div>
                <h3 className="text-xs font-bold text-slate-900">Risks by Classification</h3>
                <p className="text-[10px] text-slate-500">Master library breakdown</p>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto divide-y divide-violet-100">
              {(() => {
                const byClass = {};
                (riskUniverse || []).forEach((r) => {
                  const k = r.minorClassification || r.classification || 'Other';
                  byClass[k] = (byClass[k] || 0) + 1;
                });
                return Object.entries(byClass).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                  <div key={c} className="px-4 py-1.5 flex items-center gap-2 text-[10px]" data-testid={`class-${c.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span className="text-slate-700 flex-1 truncate">{c}</span>
                    <span className="font-bold tabular-nums text-violet-700">{n}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>

        {/* Filters + Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap" data-testid="matrix-filters">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search risk ID, item, classification, OH, division…"
              data-testid="matrix-search"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#CC0000] focus:ring-1 focus:ring-[#CC0000]/15"
            />
          </div>
          <select
            value={filterOh}
            onChange={(e) => setFilterOh(e.target.value)}
            data-testid="matrix-filter-oh"
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5"
          >
            <option value="">All Operating Heads</option>
            {Object.values(ORG_TREE).map((oh) => (
              <option key={oh.id} value={oh.id}>{oh.label}</option>
            ))}
          </select>
          <div className="inline-flex gap-1">
            {[
              { k: 'all', l: 'All' },
              { k: 'applicable', l: 'Applicable' },
              { k: 'not-applicable', l: 'Not applicable' },
              { k: 'unmapped', l: 'Unmapped' },
            ].map((s) => (
              <button
                key={s.k}
                onClick={() => setFilterStatus(s.k)}
                data-testid={`matrix-status-${s.k}`}
                className={cn('px-2.5 py-1 rounded-md text-[10px] font-bold transition',
                  filterStatus === s.k ? 'bg-[#CC0000] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100')}
              >
                {s.l}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={groupByClass}
              onChange={(e) => setGroupByClass(e.target.checked)}
              data-testid="matrix-group-toggle"
              className="rounded"
            />
            Group by classification
          </label>
          <span className="ml-auto text-[11px] text-slate-500">Showing {rows.length} of {allRows.length}</span>
        </div>

        {/* Matrix table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="matrix-table">
          {rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="matrix-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-700">No mapping rows match these filters</p>
              <p className="text-xs text-slate-500 mt-1">Clear filters or search to see the full Risk × OH × Division grid.</p>
            </div>
          ) : (
            <div className="max-h-[640px] overflow-y-auto">
              {grouped.map((g) => (
                <div key={g.key} data-testid={`matrix-group-${g.key.replace(/\s+/g, '-').toLowerCase()}`}>
                  {groupByClass && (
                    <div className="sticky top-0 z-10 px-5 py-2 bg-slate-100 border-y border-slate-200 flex items-center gap-2">
                      <Layers className="w-3 h-3 text-slate-500" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{g.key}</span>
                      <span className="text-[10px] text-slate-500">· {g.rows.length} cells</span>
                    </div>
                  )}
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[9px] text-slate-600">Risk ID</th>
                        <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[9px] text-slate-600">Risk Item</th>
                        <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[9px] text-slate-600">Operating Head</th>
                        <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[9px] text-slate-600">Division</th>
                        <th className="text-center px-3 py-2 font-bold uppercase tracking-wider text-[9px] text-slate-600">Applicable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => (
                        <tr key={r.rowId} className="border-b border-slate-100 hover:bg-slate-50/60 transition" data-testid={`row-${r.rowId}`}>
                          <td className="px-3 py-1.5 font-mono font-bold text-slate-800">{r.riskId}</td>
                          <td className="px-3 py-1.5 text-slate-700 truncate max-w-[420px]" title={r.riskItem}>{r.riskItem}</td>
                          <td className="px-3 py-1.5">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ background: r.ohColor }} />
                              <span className="text-slate-700">{r.ohLabel}</span>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-slate-700">{r.divLabel}</td>
                          <td className="px-3 py-1.5 text-center">
                            <button
                              disabled={!isAdmin}
                              data-testid={`applicable-toggle-${r.rowId}`}
                              onClick={() => {
                                toggleApplicability(r.riskId, r.ohId);
                                toast.success(`${r.applicable ? 'Removed' : 'Marked applicable'}: ${r.riskId} · ${r.ohLabel}`);
                              }}
                              className={cn(
                                'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold transition',
                                r.applicable
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200',
                                !isAdmin && 'cursor-not-allowed opacity-70',
                              )}
                            >
                              {r.applicable ? (<><CheckCircle2 className="w-3 h-3" /> Yes</>) : (<><XCircle className="w-3 h-3" /> No</>)}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3" data-testid="matrix-footer-hint">
          <ShieldCheck className="w-4 h-4 text-slate-500 mt-0.5" />
          <div className="text-[11px] text-slate-600 leading-relaxed">
            <span className="font-bold text-slate-900">System principle.</span> Risk ownership is centralised under the GRC Secretariat.
            Operating Heads <em>cannot</em> self-assign risks — they only see and operationally evaluate the risks the matrix routes to their domain.
            One master risk may carry multiple operational contextualisations (e.g. <span className="font-mono text-slate-800">R01</span> appears as <em>Middle-East export controls</em> for OSB and as <em>GST regime shift</em> for Finance).
            Use the <button onClick={() => navigate('/risk')} className="text-[#CC0000] font-bold underline">Risk Universe</button> page to see those contextualisations side-by-side.
          </div>
        </div>
      </div>
    </div>
  );
}

// Mark file as having one source of OH_META if needed elsewhere
export { ApplicabilityMatrixPage as ApplicabilityMatrix };
